/**
 * KAN-724: письма без привязки (вкладка "Не разобрано" и счётчик в меню).
 * Счётчик опрашивается раз в минуту; любое действие с письмом обновляет список,
 * счётчик, журнал писем и клиентов (письмо ушло в переписку, клиент мог появиться).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unmatchedEmailsApi } from '@/services/api/unmatchedEmails';
import { EMAILS_QUERY_KEY } from '@/hooks/useEmails';
import type { CreateClientFromEmailBody, UnmatchedEmailListParams } from '@/types/emailMailbox';

export const UNMATCHED_EMAILS_QUERY_KEY = ['emails-unmatched'] as const;
export const UNMATCHED_EMAILS_COUNT_QUERY_KEY = [...UNMATCHED_EMAILS_QUERY_KEY, 'count'] as const;
/** Опрос счётчика писем без привязки, мс. */
export const UNMATCHED_COUNT_POLL_MS = 60_000;

export function useUnmatchedEmails(params: UnmatchedEmailListParams, enabled = true) {
  return useQuery({
    queryKey: [...UNMATCHED_EMAILS_QUERY_KEY, 'list', params],
    queryFn: () => unmatchedEmailsApi.list(params),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    enabled,
  });
}

export function useUnmatchedEmail(id: string | null) {
  return useQuery({
    queryKey: [...UNMATCHED_EMAILS_QUERY_KEY, 'detail', id],
    queryFn: () => unmatchedEmailsApi.get(id as string),
    enabled: !!id,
  });
}

export function useUnmatchedEmailCount() {
  const { data } = useQuery({
    queryKey: UNMATCHED_EMAILS_COUNT_QUERY_KEY,
    queryFn: unmatchedEmailsApi.count,
    refetchInterval: UNMATCHED_COUNT_POLL_MS,
    staleTime: 30_000,
  });
  return typeof data?.count === 'number' ? data.count : 0;
}

function useInvalidateAfterAction() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: UNMATCHED_EMAILS_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: EMAILS_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: ['clients'] });
  };
}

export function useAssignUnmatchedEmail() {
  const onSuccess = useInvalidateAfterAction();
  return useMutation({
    mutationFn: ({ id, clientId }: { id: string; clientId: string }) => unmatchedEmailsApi.assign(id, clientId),
    onSuccess,
  });
}

export function useCreateClientFromEmail() {
  const onSuccess = useInvalidateAfterAction();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CreateClientFromEmailBody }) =>
      unmatchedEmailsApi.createClient(id, body),
    onSuccess,
  });
}

export function useDismissUnmatchedEmail() {
  const onSuccess = useInvalidateAfterAction();
  return useMutation({
    mutationFn: (id: string) => unmatchedEmailsApi.dismiss(id),
    onSuccess,
  });
}
